<template>
  <div
    class="notification-holder"
    :style="`background-color:${
      notification.seen_at ? '#e3e3e3' : actionColor
    }`"
  >
    <span
      class="notification-text-holder"
      :style="`background-color:${
        notification.seen_at ? '#fff' : actionColor
      }; color:${notification.seen_at ? '#000' : '#fff'}`"
    >
      {{ actionBadgeLabel }}
    </span>
    <span :style="`color:${notification.seen_at ? '#000' : '#fff'}`">
      {{ notificationMessage }}
    </span>
  </div>
</template>

<script>
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

const ACTION_COLORS = Object.freeze({
  award: "#008566",
  redeem: "#4a7cab",
  correction: "#f5a623"
})

export default {
  name: "StudentPbisPointNotification",
  props: {
    notification: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const { t } = useI18n()
    const store = useStore()

    const behaviourTerminology = computed(
      () => store.getters["behaviourSettings/behaviourTerminology"] || {}
    )

    const normalizedAction = computed(() =>
      String(props.notification?.action ?? "")
        .trim()
        .toLowerCase()
    )

    const actionColor = computed(
      () => ACTION_COLORS[normalizedAction.value] || "#4a7cab"
    )

    const pointsValue = computed(() => {
      const points = Number(props.notification?.points)
      return Number.isFinite(points) ? Math.abs(points) : 0
    })

    const staffName = computed(() => {
      const createdBy = props.notification?.created_by
      const name =
        `${createdBy?.first_name ?? ""} ${createdBy?.last_name ?? ""}`.trim()
      return name || t("behaviour_manage.notification_pbis_staff_fallback")
    })

    const reasonText = computed(() =>
      String(props.notification?.reason ?? "").trim()
    )

    const pointsLabel = computed(
      () =>
        behaviourTerminology.value?.points ||
        t("behaviour_manage.notification_pbis_points_fallback")
    )

    const actionBadgeLabel = computed(() => {
      switch (normalizedAction.value) {
        case "award":
          return t("behaviour_manage.notification_pbis_badge_award")
        case "redeem":
          return t("behaviour_manage.notification_pbis_badge_redeem")
        case "correction":
          return t("behaviour_manage.notification_pbis_badge_correction")
        default:
          return t("behaviour_manage.notification_pbis_badge_default")
      }
    })

    const notificationMessage = computed(() => {
      const points = pointsValue.value
      const reason = reasonText.value
      const staff = staffName.value
      const label = pointsLabel.value
      const reasonSuffix = reason
        ? t("behaviour_manage.notification_pbis_reason_suffix", { reason })
        : ""

      switch (normalizedAction.value) {
        case "award":
          return t("behaviour_manage.notification_pbis_award", {
            points,
            pointsLabel: label,
            staff,
            reason:
              reason || t("behaviour_manage.notification_pbis_reason_fallback")
          })
        case "redeem":
          return (
            t("behaviour_manage.notification_pbis_redeem", {
              points,
              pointsLabel: label
            }) + reasonSuffix
          )
        case "correction":
          return (
            t("behaviour_manage.notification_pbis_correction", {
              pointsLabel: label,
              staff
            }) + reasonSuffix
          )
        default:
          return (
            t("behaviour_manage.notification_pbis_default", {
              pointsLabel: label,
              staff
            }) + reasonSuffix
          )
      }
    })

    return {
      actionColor,
      actionBadgeLabel,
      notificationMessage
    }
  }
}
</script>
